import React, {useContext, useEffect, useMemo, useState} from "react";
import debounce from "lodash.debounce";
import {
    Alert,
    Box,
    Dialog, DialogActions, DialogContent, DialogContentText,
    DialogTitle,
    FormControl, InputAdornment,
    InputLabel,
    MenuItem,
    Select, Snackbar,
    TextField,
    ToggleButton
} from "@mui/material";
import {useDropzone} from "react-dropzone";
import './step-1.scss';
import HourGlassAnimation from './hourglass.gif';
import {Replay} from "@mui/icons-material";
import Typography from "@mui/material/Typography";
import {ButtonIcapeOutlinedRed, ButtonIcapeSoftBlue} from "../../../button/button-icape";
import CalculatorHdiContext from "../../../../context/calculatorHdiProvider";
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import {useTranslation} from "react-i18next";
import {inchToMm, mmToInch} from "../../../../tools/unit.utils";
import BrokenImageIcon from "@mui/icons-material/BrokenImage";
import LockIcon from "@mui/icons-material/Lock";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import GerberDropzone from "../../components/gerber-dropzone";
import DeliveryFormatSwitch from "../../components/delivery-format-switch";
import {extremeSizeValues, maxPcbCalculator} from "../../../../tools/quantity-checks";

function Step1(props) {
    const {t} = useTranslation();

    const {
        isJaydaPcbQuotable, repeatOrderMode,
        quantity, setQuantity,
        unitFormat,
        analyzeInProgress,
        technologyId, setTechnologyId,
        pcbLength, setPcbLength,
        pcbWidth, setPcbWidth,
        panelLength, setPanelLength,
        panelWidth, setPanelWidth,
        solderMaskId, setSolderMaskId,
        panelQuantity, setPanelQuantity,
        pcbPerPanel, setPcbPerPanel,
        xoutId, setXoutId,
        userGerber, setUserGerber,
        pcbDeliveryFormat, setPcbDeliveryFormat,
        isLockFields,
        isStepOneToFinalize,
        pcbLengthIn, setPcbLengthIn,
        pcbWidthIn, setPcbWidthIn,
        panelLengthIn, setPanelLengthIn,
        panelWidthIn, setPanelWidthIn
    } = useContext(CalculatorHdiContext);

    const handleNewProject = props.handleNewProject;
    const handleDropFile = props.handleDropFile;


    // PANEL QUANTITY AUTOMATION
    useEffect(() => {
        // if ((quantity !== null && quantity !== '') && (pcbPerPanel !== null && pcbPerPanel !== '')) {
        //     setPanelQuantity(Math.ceil(quantity / pcbPerPanel));
        // }
        if((panelQuantity !== null && panelQuantity !== '') && (pcbPerPanel !== null && pcbPerPanel !== '') && (pcbDeliveryFormat === 'classic' || pcbDeliveryFormat === 'custom')){
            setQuantity(panelQuantity * pcbPerPanel);
        }
    }, [panelQuantity, pcbPerPanel, pcbDeliveryFormat])


    // RESET DATA
    const [isResetFieldsDialogOpen, setIsResetFieldsDialogOpen] = useState(false);
    const handleResetFieldsDialog = () => {
        setIsResetFieldsDialogOpen(!isResetFieldsDialogOpen);
        handleNewProject();
    }


    // PCB SIZE / PANEL SIZE CHECK MM - INCH -----------------------------------------
    //mm var
    const minPcbWidth = 10;
    const minPcbLength = 15;
    const maxPcbWidth = 480;
    const maxPcbLength = 580;

    const minPanelWidth = 50;
    const minPanelLength = 50;
    const maxPanelWidth = 550;
    const maxPanelLength = 600;

    //inch var
    const minPcbWidthIn = parseFloat(mmToInch(minPcbWidth));
    const minPcbLengthIn = parseFloat(mmToInch(minPcbLength));
    const maxPcbWidthIn = 18.89;//parseFloat(mmToInch(maxPcbWidth)); avoiding conversion > 480 for mm
    const maxPcbLengthIn = parseFloat(mmToInch(maxPcbLength));

    const minPanelWidthIn = parseFloat(mmToInch(minPanelWidth));
    const minPanelLengthIn = parseFloat(mmToInch(minPanelLength));
    const maxPanelWidthIn = parseFloat(mmToInch(maxPanelWidth));
    const maxPanelLengthIn = parseFloat(mmToInch(maxPanelLength));

    //debounce delay
    const debounceDelay = 800;


    // handle PCB length field in mm and inch
    const handleMmInchPcbLength = (newValue) => {
        if(unitFormat === 'mm'){
            setPcbLength(newValue);
            setPcbLengthIn(mmToInch(newValue));
        }else{
            setPcbLength(inchToMm(newValue));
            setPcbLengthIn(newValue);
        }
    }
    const handleMmInchPcbLengthCheck = (_unitFormat, _pcbLength, _pcbLengthIn) => {
        // EMPTY
        if(_pcbLength === '' || _pcbLengthIn === ''){
            setPcbLength('');
            setPcbLengthIn('');
            return;
        }

        // Float the field value
        /*        _pcbLength = stringToFloat2Dec(_pcbLength);
                _pcbLengthIn = stringToFloat2Dec(_pcbLengthIn);*/

        // Check Unit
        if(_unitFormat === 'mm'){
            // MAX mm
            if (_pcbLength > maxPcbLength) {
                setPcbLength(maxPcbLength);
                setPcbLengthIn(maxPcbLengthIn);
                return;
            }

            // MIN mm
            if (_pcbLength < minPcbLength) {
                setPcbLength(minPcbLength);
                setPcbLengthIn(minPcbLengthIn);
                return;
            }

            // VALUE INPUT mm
            setPcbLength(_pcbLength);
            setPcbLengthIn(mmToInch(_pcbLength));

        }else{
            // MAX inch
            if (_pcbLengthIn > maxPcbLengthIn) {
                setPcbLength(maxPcbLength);
                setPcbLengthIn(maxPcbLengthIn);
                return;
            }

            // MIN inch
            if (_pcbLengthIn < minPcbLengthIn) {
                setPcbLength(maxPcbLength);
                setPcbLengthIn(maxPcbLengthIn);
                return;
            }

            // VALUE INPUT inch
            setPcbLength(inchToMm(_pcbLengthIn));
            setPcbLengthIn(_pcbLengthIn);
        }
    };


    // handle PCB width field in mm and inch
    const handleMmInchPcbWidth = (newValue) => {
        if(unitFormat === 'mm'){
            setPcbWidth(newValue);
            setPcbWidthIn(mmToInch(newValue));
        }else{
            setPcbWidth(inchToMm(newValue));
            setPcbWidthIn(newValue);
        }
    };
    const handleMmInchPcbWidthCheck = (_unitFormat, _pcbWidth, _pcbWidthIn) => {
        // EMPTY
        if(_pcbWidth === '' || _pcbWidthIn === ''){
            setPcbWidth('');
            setPcbWidthIn('');
            return;
        }

        // Check Unit
        if(_unitFormat === 'mm'){
            // MAX mm
            if (_pcbWidth > maxPcbWidth) {
                setPcbWidth(maxPcbWidth);
                setPcbWidthIn(maxPcbWidthIn);
                return;
            }

            // MIN mm
            if (_pcbWidth < minPcbWidth) {
                setPcbWidth(minPcbWidth);
                setPcbWidthIn(minPcbWidthIn);
                return;
            }

            // VALUE INPUT mm
            setPcbWidth(_pcbWidth);
            setPcbWidthIn(mmToInch(_pcbWidth));

        }else{
            // MAX inch
            if (_pcbWidthIn > maxPcbWidthIn) {
                setPcbWidth(maxPcbWidth);
                setPcbWidthIn(maxPcbWidthIn);
                return;
            }

            // MIN inch
            if (_pcbWidthIn < minPcbWidthIn) {
                setPcbWidth(maxPcbWidth);
                setPcbWidthIn(maxPcbWidthIn);
                return;
            }

            // VALUE INPUT inch
            setPcbWidth(inchToMm(_pcbWidthIn));
            setPcbWidthIn(_pcbWidthIn);
        }
    };


    // handle PANEL length field in mm and inch
    const handleMmInchPanelLength = (newValue) => {
        if(unitFormat === 'mm'){
            setPanelLength(newValue);
            setPanelLengthIn(mmToInch(newValue));
        }else{
            setPanelLength(inchToMm(newValue));
            setPanelLengthIn(newValue);
        }
    }
    const handleMmInchPanelLengthCheck = (_unitFormat, _panelLength, _panelLengthIn, _pcbLength, _pcbLengthIn) => {
        // EMPTY
        if(_panelLength === '' || _panelLengthIn === ''){
            setPanelLength('');
            setPanelLengthIn('');
            return;
        }

        // Check Unit
        if(_unitFormat === 'mm'){
            // MAX mm
            if (_panelLength > maxPanelLength) {
                setPanelLength(maxPanelLength);
                setPanelLengthIn(maxPanelLengthIn);
                return;
            }

            // MAX pcb mm
            if (_pcbLength > maxPcbLength) {
                setPanelLength(maxPcbLength);
                setPanelLengthIn(maxPcbLengthIn);
                return;
            }

            // MIN mm & PANEL SMALLER THAN PCB
            if(_panelLength < minPanelLength && _pcbLength < minPanelLength){
                setPanelLength(minPanelLength);
                setPanelLengthIn(minPanelLengthIn);
                return;
            }

            // PANEL SMALLER THAN PCB
            if(parseFloat(_panelLength) < parseFloat(_pcbLength)){
                setPanelLength(_pcbLength);
                setPanelLengthIn(mmToInch(_pcbLength));
                return;
            }

            // VALUE INPUT mm
            setPanelLength(_panelLength);
            setPanelLengthIn(mmToInch(_panelLength));

        }else{
            // MAX inch
            if (_panelLengthIn > maxPanelLengthIn) {
                setPanelLength(maxPanelLength);
                setPanelLengthIn(maxPanelLengthIn);
                return;
            }

            // MAX pcb inch
            if (_pcbLengthIn > maxPcbLengthIn) {
                setPanelLength(maxPcbLength);
                setPanelLengthIn(maxPcbLengthIn);
                return;
            }

            // MIN inch & PANEL SMALLER THAN PCB
            if(_panelLengthIn < minPanelLengthIn && _pcbLengthIn < minPanelLengthIn){
                setPanelLength(minPanelLength);
                setPanelLengthIn(minPanelLengthIn);
                return;
            }

            // PANEL SMALLER THAN PCB
            if(parseFloat(_panelLengthIn) < parseFloat(_pcbLengthIn)){
                setPanelLength(inchToMm(_pcbLengthIn));
                setPanelLengthIn(_pcbLengthIn);
                return;
            }

            // VALUE INPUT inch
            setPanelLength(inchToMm(_panelLengthIn));
            setPanelLengthIn(_panelLengthIn);
        }
    };


    // handle PANEL width field in mm and inch
    const handleMmInchPanelWidth = (newValue) => {
        if(unitFormat === 'mm'){
            setPanelWidth(newValue);
            setPanelWidthIn(mmToInch(newValue));
        }else{
            setPanelWidth(inchToMm(newValue));
            setPanelWidthIn(newValue);
        }
    }
    const handleMmInchPanelWidthCheck = (_unitFormat, _panelWidth, _panelWidthIn, _pcbWidth, _pcbWidthIn) => {
        // EMPTY
        if(_panelWidth === '' || _panelWidthIn === ''){
            setPanelWidth('');
            setPanelWidthIn('');
            return;
        }

        // Check Unit
        if(_unitFormat === 'mm'){
            // MAX mm
            if (_panelWidth > maxPanelWidth) {
                setPanelWidth(maxPanelWidth);
                setPanelWidthIn(maxPanelWidthIn);
                return;
            }

            // MAX pcb mm
            if (_pcbWidth > maxPcbWidth) {
                setPanelWidth(maxPcbWidth);
                setPanelWidthIn(maxPcbWidthIn);
                return;
            }

            // MIN mm & PANEL SMALLER THAN PCB
            if(_panelWidth < minPanelLength && _pcbWidth < minPanelLength){
                setPanelWidth(minPanelWidth);
                setPanelWidthIn(minPanelWidthIn);
                return;
            }

            // PANEL SMALLER THAN PCB
            if(parseFloat(_panelWidth) < parseFloat(_pcbWidth)){
                setPanelWidth(_pcbWidth);
                setPanelWidthIn(mmToInch(_pcbWidth));
                return;
            }

            // VALUE INPUT mm
            setPanelWidth(_panelWidth);
            setPanelWidthIn(mmToInch(_panelWidth));

        }else{
            // MAX inch
            if (_panelWidthIn > maxPanelWidthIn) {
                setPanelWidth(maxPanelWidth);
                setPanelWidthIn(maxPanelWidthIn);
                return;
            }

            // MAX pcb inch
            if (_pcbWidthIn > maxPcbWidthIn) {
                setPanelWidth(maxPcbWidth);
                setPanelWidthIn(maxPcbWidthIn);
                return;
            }

            // MIN inch & PANEL SMALLER THAN PCB
            if(_panelWidthIn < minPanelWidthIn && _pcbWidthIn < minPanelWidthIn){
                setPanelWidth(minPanelWidth);
                setPanelWidthIn(minPanelWidthIn);
                return;
            }

            // PANEL SMALLER THAN PCB
            if(parseFloat(_panelWidthIn) < parseFloat(_pcbWidthIn)){
                setPanelWidth(inchToMm(_pcbWidthIn));
                setPanelWidthIn(_pcbWidthIn);
                return;
            }

            // VALUE INPUT inch
            setPanelWidth(inchToMm(_panelWidthIn));
            setPanelWidthIn(_panelWidthIn);
        }
    };


    const handlePcbPerPanel = (nbPcb) => {
       if (nbPcb > maxPcbCalculator(pcbWidth, pcbLength, panelWidth, panelLength) || nbPcb < 1) {
           return
       }
        setPcbPerPanel(nbPcb);
    }


    // Checking all dimensions
    const dimensionsCheck = (_unitFormat, _pcbLength, _pcbLengthIn, _pcbWidth, _pcbWidthIn, _panelLength, _panelLengthIn, _panelWidth, _panelWidthIn) => {
        handleMmInchPcbLengthCheck(_unitFormat, _pcbLength, _pcbLengthIn);
        handleMmInchPcbWidthCheck(_unitFormat, _pcbWidth, _pcbWidthIn);
        handleMmInchPanelLengthCheck(_unitFormat, _panelLength, _panelLengthIn, _pcbLength, _pcbLengthIn);
        handleMmInchPanelWidthCheck(_unitFormat, _panelWidth, _panelWidthIn, _pcbWidth, _pcbWidthIn);
    }

    //debounce useEffect to give the user time to type any number in fields.
    const debounceDimensions = useMemo(() => debounce(dimensionsCheck, debounceDelay), []);

    // DEBOUNCE LAUNCHER
    useEffect(() => {
        debounceDimensions(unitFormat, pcbLength, pcbLengthIn, pcbWidth, pcbWidthIn, panelLength, panelLengthIn, panelWidth, panelWidthIn);
    },[pcbLength, pcbLengthIn, pcbWidth, pcbWidthIn, panelLength, panelLengthIn, panelWidth, panelWidthIn]);
    // Stop the invocation of the debounced function after unmounting
    useEffect(() => {
        return () => {
            debounceDimensions.cancel();
        }
    }, []);


    return (
        <>
            {/*SWITCHER + DRAG N DROP*/}
            <Box className={"technical-data-card-column step1-form-settings"}>

                {/*DRAG N DROP GERBER*/}
                <GerberDropzone
                    gerber={{userGerber, setUserGerber, handleDropFile}}
                    jayda={{analyzeInProgress, isJaydaPcbQuotable}}
                    lock={{repeatOrderMode, isLockFields}}/>

                {/*SWITCHER*/}
                <DeliveryFormatSwitch
                    pcbDeliveryFormat={{pcbDeliveryFormat, setPcbDeliveryFormat}}
                    panelization={{isCustomPanelization: false, handleShowPanelization: false, lockCustomPanelizationFields: false}}
                    isLockFields={isLockFields}/>
            </Box>

            {/*SINGLE PCB / GERBER PANELIZATION*/}
            <Box className="technical-data-card step1-settings">

                {/*TITLE*/}
                { pcbDeliveryFormat === 'single' ?
                    <>
                        {/*TITLE SINGLE PCB*/}
                        <Box className="step1-calculator-title">
                            <Typography variant={'h3'}>{t("calculators.step1.deliveryFormat.singlePcb")}</Typography>
                            {(analyzeInProgress === 'stop' || analyzeInProgress === 'ended') && !isLockFields &&
                                <Box className='step1-reset-data' onClick={ () => setIsResetFieldsDialogOpen(true) }>
                                    <span>{t("common.resetFields")}</span><Replay/>
                                </Box>
                            }
                        </Box>
                    </>
                    :
                    (pcbDeliveryFormat === 'classic' || pcbDeliveryFormat === 'custom') &&
                    <>
                        {/*TITLE PANELIZATION*/}
                        <Box className="step1-calculator-title">
                            <Typography variant={'h3'}>{t("calculators.step1.deliveryFormat.panelization")}</Typography>
                            {(analyzeInProgress === 'stop' || analyzeInProgress === 'ended') && !isLockFields &&
                                <Box className='step1-reset-data' onClick={ () => setIsResetFieldsDialogOpen(true) }>
                                    <span>{t("common.resetFields")}</span><Replay/>
                                </Box>
                            }
                        </Box>
                    </>
                }

                {/*FIELDS*/}
                <Box className="technical-data-card-column-fields">

                    {/*PCB LENGTH*/}
                    <TextField
                        id="pcbLength"
                        label={t("calculators.step1.fields.pcbLength")}
                        value={unitFormat === 'mm' ? pcbLength : pcbLengthIn}
                        onChange={e => handleMmInchPcbLength(e.target.value)}
                        variant="outlined"
                        margin="normal"
                        type="number"
                        required
                        disabled={isLockFields}
                        error={pcbLength === '' && isStepOneToFinalize}
                        helperText={`${t("common.dimensions")} ${extremeSizeValues("pcb","length", unitFormat)}`}
                        InputProps={{
                            endAdornment: <InputAdornment position="end">{unitFormat}</InputAdornment>,
                        }}
                    />

                    {/*PCB WIDTH*/}
                    <TextField
                        id="pcbWidth"
                        label={t("calculators.step1.fields.pcbWidth")}
                        value={unitFormat === 'mm' ? pcbWidth : pcbWidthIn}
                        onChange={e => handleMmInchPcbWidth(e.target.value)}
                        variant="outlined"
                        margin="normal"
                        type="number"
                        required
                        disabled={isLockFields}
                        error={pcbWidth === '' && isStepOneToFinalize}
                        helperText={`${t("common.dimensions")} ${extremeSizeValues("pcb","width", unitFormat)}`}
                        InputProps={{
                            endAdornment: <InputAdornment position="end">{unitFormat}</InputAdornment>,
                        }}
                    />

                    {
                        (pcbDeliveryFormat === 'classic' || pcbDeliveryFormat === 'custom') &&
                        <>
                            {/* PANEL LENGTH */}
                            <TextField
                                id="panelLength"
                                label={t("calculators.step1.fields.panelLength")}
                                value={unitFormat === 'mm' ? panelLength : panelLengthIn}
                                onChange={e => handleMmInchPanelLength(e.target.value)}
                                variant="outlined"
                                margin="normal"
                                type="number"
                                required
                                disabled={isLockFields && repeatOrderMode.background}
                                error={panelLength === '' && isStepOneToFinalize}
                                helperText={`${t("common.dimensions")} ${extremeSizeValues("panel","length", unitFormat)}`}
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">{unitFormat}</InputAdornment>,
                                }}
                            />

                            {/* PANEL WIDTH */}
                            <TextField
                                id="panelWidth"
                                label={t("calculators.step1.fields.panelWidth")}
                                value={unitFormat === 'mm' ? panelWidth : panelWidthIn}
                                onChange={e => handleMmInchPanelWidth(e.target.value)}
                                variant="outlined"
                                margin="normal"
                                type="number"
                                required
                                disabled={isLockFields && repeatOrderMode.background}
                                error={panelWidth === '' && isStepOneToFinalize}
                                helperText={`${t("common.dimensions")} ${extremeSizeValues("panel","width", unitFormat)}`}
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">{unitFormat}</InputAdornment>,
                                }}
                            />

                            {/* PCB PER PANEL */}
                            <TextField
                                id="pcbPerPanel"
                                label={t("calculators.step1.fields.pcbPerPanel")}
                                value={pcbPerPanel}
                                onChange={e => handlePcbPerPanel(e.target.value)}
                                variant="outlined"
                                margin="normal"
                                type="number"
                                required
                                disabled={isLockFields && repeatOrderMode.background}
                                error={pcbPerPanel === '' && isStepOneToFinalize}
                            />
                        </>
                    }

                    {/* SOLDER MASK Field */}
                    <FormControl disabled={isLockFields} required
                                 error={solderMaskId === '' && isStepOneToFinalize}>
                        <InputLabel id="solderMask-label">{t("calculators.step1.fields.solderMask")}</InputLabel>
                        <Select
                            labelId="solderMask-label"
                            id="solderMask"
                            value={solderMaskId}
                            label={t("calculators.step1.fields.solderMask")}
                            onChange={e => setSolderMaskId(e.target.value)}

                        >
                            <MenuItem value={1}>{t("calculators.selectable.noSolderMask")}</MenuItem>
                            <MenuItem value={2}>{t("calculators.selectable.top")}</MenuItem>
                            <MenuItem value={3}>{t("calculators.selectable.bottom")}</MenuItem>
                            <MenuItem value={4}>{t("calculators.selectable.topBottom")}</MenuItem>
                        </Select>
                    </FormControl>

                    {/* STACK UP Field */}
                    <FormControl disabled={isLockFields} required
                                 error={technologyId === '' && isStepOneToFinalize}>
                        <InputLabel id="stackUp-label">{t("calculators.step1.fields.stackUp")}</InputLabel>
                        <Select
                            labelId="stackUp-label"
                            id="stackUp"
                            value={technologyId}
                            label={t("calculators.step1.fields.stackUp")}
                            onChange={(e) => setTechnologyId(e.target.value)}
                        >
                            <MenuItem value={21} disabled={true}>{t("calculators.selectable.singleSide")}</MenuItem>
                            <MenuItem value={22} disabled={true}>{t("calculators.selectable.doubleSide")}</MenuItem>
                            <MenuItem value={23}>4 {t("common.layers")}</MenuItem>
                            <MenuItem value={24}>6 {t("common.layers")}</MenuItem>
                            <MenuItem value={25}>8 {t("common.layers")}</MenuItem>
                            <MenuItem value={26}>10 {t("common.layers")}</MenuItem>
                            <MenuItem value={27}>12 {t("common.layers")}</MenuItem>
                        </Select>
                    </FormControl>

                    {/*XOUT*/}
                    { (pcbDeliveryFormat === 'classic' || pcbDeliveryFormat === 'custom') &&
                        <>
                            {/* XOUT Field */}
                            <FormControl disabled={isLockFields} required
                                         error={xoutId === '' && isStepOneToFinalize}>
                                <InputLabel id="xOut-label">{t("calculators.step1.fields.xOut")}</InputLabel>
                                <Select
                                    labelId="xOut-label"
                                    id="xOut"
                                    value={xoutId}
                                    label={t("calculators.step1.fields.xOut")}
                                    onChange={(e) => setXoutId(e.target.value)}
                                >
                                    <MenuItem value={1}>{t("common.yes")} (10%)</MenuItem>
                                    <MenuItem value={2}>{t("common.no")}</MenuItem>
                                </Select>
                            </FormControl>
                        </>}

                </Box>


                {/*RESET ALL DATA POPUP MODAL*/}
                <Dialog open={isResetFieldsDialogOpen} onClose={ () => setIsResetFieldsDialogOpen(false)}>
                    <DialogTitle>
                        {t("popup.resetFields.title")}
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            {t("popup.resetFields.content")}
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <ButtonIcapeOutlinedRed onClick={handleResetFieldsDialog}>
                            {t("cta.reset")}
                        </ButtonIcapeOutlinedRed>
                        <ButtonIcapeSoftBlue onClick={ () => setIsResetFieldsDialogOpen(false) }>
                            {t("cta.cancel")}
                        </ButtonIcapeSoftBlue>
                    </DialogActions>
                </Dialog>

            </Box>
        </>
    )
}

export default Step1;
